import { useState, useEffect, useMemo, useRef } from 'react';
import { Building2, ChevronDown, Loader2, Users, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Agency } from '../../types/agency';

// ─── Constants ───────────────────────────────────────────────────────────────

const MONTHS = [
  { key: '04', label: 'AVRIL',     quarter: 1 },
  { key: '05', label: 'MAI',       quarter: 1 },
  { key: '06', label: 'JUIN',      quarter: 1 },
  { key: '07', label: 'JUILLET',   quarter: 2 },
  { key: '08', label: 'AOÛT',      quarter: 2 },
  { key: '09', label: 'SEPTEMBRE', quarter: 2 },
  { key: '10', label: 'OCTOBRE',   quarter: 3 },
  { key: '11', label: 'NOVEMBRE',  quarter: 3 },
  { key: '12', label: 'DÉCEMBRE',  quarter: 3 },
  { key: '01', label: 'JANVIER',   quarter: 4 },
  { key: '02', label: 'FÉVRIER',   quarter: 4 },
  { key: '03', label: 'MARS',      quarter: 4 },
] as const;

const QUARTERS = [
  { n: 1, label: 'TRIMESTRE 1', months: ['04', '05', '06'] as const },
  { n: 2, label: 'TRIMESTRE 2', months: ['07', '08', '09'] as const },
  { n: 3, label: 'TRIMESTRE 3', months: ['10', '11', '12'] as const },
  { n: 4, label: 'TRIMESTRE 4', months: ['01', '02', '03'] as const },
];

const fmt = (v: number): string =>
  !v ? '—' : new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Math.round(v));

const pct = (real: number, obj: number) =>
  obj > 0 ? Math.round((real / obj) * 100) : 0;

// ─── Types ───────────────────────────────────────────────────────────────────

type ViewMode = 'agence' | 'negociateur';

interface SyntheseColumn {
  key:          string;
  label:        string;
  getObjective: (mk: string) => number;
  getRealised:  (mk: string) => number;
  getPotentiel: () => number;           // total pipeline (not monthly)
}

interface AgencySyntheseData {
  name:              string;
  monthlyObjectives: Record<string, number>;
}

interface NegotiatorSynthese {
  id:                string;
  firstname:         string;
  lastname:          string;
  monthlyObjectives: Record<string, number>;
}

type TableRow =
  | { type: 'month';   monthKey: string; label: string }
  | { type: 'quarter'; quarterN: number; label: string; months: readonly string[] };

interface SyntheseAgenceProps {
  agencies:   Agency[];
  fiscalYear: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildMonthlyMap(): Map<string, Map<string, number>> {
  const map = new Map<string, Map<string, number>>();
  MONTHS.forEach(m => map.set(m.key, new Map()));
  return map;
}

function agencyMonthlyObjectives(agencyData: any, fiscalYear: number): Record<string, number> {
  const out: Record<string, number> = {};
  if (agencyData?.has_custom_objectives && agencyData.monthly_objectives?.[fiscalYear]) {
    const c = agencyData.monthly_objectives[fiscalYear];
    MONTHS.forEach(m => { out[m.key] = c[m.key] || 0; });
  } else {
    const entry  = agencyData?.agency_annual_objectives?.find((o: any) => o.fiscal_year === fiscalYear);
    const annual = entry?.annual_objective || 0;
    MONTHS.forEach(m => { out[m.key] = annual / 12; });
  }
  return out;
}

function netFees(tx: any): number {
  const retro = (tx.transaction_retrocessions || []).reduce((s: number, r: any) => s + r.amount, 0);
  return tx.fees_without_tax - retro;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function SyntheseAgence({ agencies, fiscalYear }: SyntheseAgenceProps) {
  const [viewMode, setViewMode]               = useState<ViewMode>('agence');
  const [selectedAgencies, setSelectedAgencies] = useState<Set<string>>(new Set());
  const [showPicker, setShowPicker]           = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  // ── Vue Agence state ──
  const [agData,      setAgData]      = useState<AgencySyntheseData[]>([]);
  const [agRealised,  setAgRealised]  = useState<Map<string, Map<string, number>>>(new Map());
  const [agPotentiel, setAgPotentiel] = useState<Map<string, number>>(new Map()); // name → total

  // ── Vue Négociateur state ──
  const [negData,      setNegData]      = useState<NegotiatorSynthese[]>([]);
  const [negRealised,  setNegRealised]  = useState<Map<string, Map<string, number>>>(new Map());
  const [negPotentiel, setNegPotentiel] = useState<Map<string, number>>(new Map()); // id → total

  // ── Sync agencies selection ──
  useEffect(() => {
    if (agencies.length === 0) return;
    setSelectedAgencies(prev => {
      if (prev.size === 0) return new Set(agencies.map(a => a.name));
      const valid    = new Set(agencies.map(a => a.name));
      const filtered = new Set(Array.from(prev).filter(n => valid.has(n)));
      return filtered.size > 0 ? filtered : new Set(agencies.map(a => a.name));
    });
  }, [agencies]);

  // ── Close picker on outside click ──
  useEffect(() => {
    if (!showPicker) return;
    const handle = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node))
        setShowPicker(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showPicker]);

  // ── Trigger load ──
  useEffect(() => {
    if (selectedAgencies.size === 0 || agencies.length === 0) return;
    if (viewMode === 'agence') void loadAgenceData();
    else                       void loadNegociateurData();
  }, [viewMode, selectedAgencies, fiscalYear]);

  // ── Picker helpers ──
  const toggleAgency = (name: string) => {
    setSelectedAgencies(prev => {
      const next = new Set(prev);
      if (next.has(name) && next.size > 1) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleAllAgencies = () => {
    setSelectedAgencies(prev =>
      prev.size === agencies.length
        ? new Set([agencies[0].name])
        : new Set(agencies.map(a => a.name))
    );
  };

  // ─── Loader: Vue Agence ───────────────────────────────────────────────────

  const loadAgenceData = async () => {
    setLoading(true);
    setError('');
    try {
      const names     = Array.from(selectedAgencies);
      const startDate = `${fiscalYear}-04-01`;
      const endDate   = `${fiscalYear + 1}-03-31`;

      const TX_SELECT = `
        fees_without_tax, status, compromise_date,
        transaction_retrocessions(amount),
        transaction_negotiators(negotiator_id, percentage, agency)
      `;

      const [
        { data: agRow,       error: agErr    },
        { data: txReal,      error: txRErr   },
        { data: txPot,       error: txPErr   },
        { data: negAgencies, error: negAgErr },
      ] = await Promise.all([
        supabase.from('agencies')
          .select(`name, has_custom_objectives, monthly_objectives,
                   agency_annual_objectives!left(fiscal_year, annual_objective)`)
          .in('name', names),

        supabase.from('transactions').select(TX_SELECT)
          .in('status', ['compromis', 'acte', 'encaisse', 'cloture'])
          .gte('compromise_date', startDate)
          .lte('compromise_date', endDate),

        supabase.from('transactions').select(TX_SELECT)
          .eq('status', 'potentiel'),

        supabase.from('negotiator_agencies')
          .select('negotiator_id, agency, start_date, end_date'),
      ]);

      if (agErr)    throw agErr;
      if (txRErr)   throw txRErr;
      if (txPErr)   throw txPErr;
      if (negAgErr) throw negAgErr;

      // Résoudre l'agence d'une ligne : tn.agency en priorité, sinon via negotiator_agencies
      const resolveAgency = (negotiatorId: string, txDate?: Date): string | null => {
        const assocs = (negAgencies || []).filter((na: any) => na.negotiator_id === negotiatorId);
        if (assocs.length === 0) return null;
        if (txDate) {
          const active = assocs.find((na: any) => {
            const s = na.start_date ? new Date(na.start_date) : null;
            const e = na.end_date   ? new Date(na.end_date)   : null;
            return (!s || s <= txDate) && (!e || e >= txDate);
          });
          if (active) return active.agency;
        }
        return assocs[assocs.length - 1].agency;
      };

      const agMap = new Map((agRow || []).map((a: any) => [a.name, a]));

      const syntheseAg: AgencySyntheseData[] = agencies
        .filter(a => selectedAgencies.has(a.name))
        .map(a => ({
          name:              a.name,
          monthlyObjectives: agencyMonthlyObjectives(agMap.get(a.name), fiscalYear),
        }));

      // Realised map: monthKey → agencyName → amount
      const realisedMap = buildMonthlyMap();
      (txReal || []).forEach((tx: any) => {
        if (!tx.compromise_date) return;
        const mk     = String(new Date(tx.compromise_date).getMonth() + 1).padStart(2, '0');
        const mm     = realisedMap.get(mk);
        if (!mm) return;
        const fees   = netFees(tx);
        const txDate = new Date(tx.compromise_date);
        (tx.transaction_negotiators || []).forEach((tn: any) => {
          const agency = tn.agency || resolveAgency(tn.negotiator_id, txDate);
          if (agency && names.includes(agency))
            mm.set(agency, (mm.get(agency) || 0) + (fees * tn.percentage) / 100);
        });
      });

      // Potentiel map: agencyName → total
      const potMap = new Map<string, number>();
      (txPot || []).forEach((tx: any) => {
        const fees = netFees(tx);
        (tx.transaction_negotiators || []).forEach((tn: any) => {
          const agency = tn.agency || resolveAgency(tn.negotiator_id);
          if (agency && names.includes(agency))
            potMap.set(agency, (potMap.get(agency) || 0) + (fees * tn.percentage) / 100);
        });
      });

      setAgData(syntheseAg);
      setAgRealised(realisedMap);
      setAgPotentiel(potMap);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Loader: Vue Négociateur ──────────────────────────────────────────────

  const loadNegociateurData = async () => {
    setLoading(true);
    setError('');
    try {
      const names     = Array.from(selectedAgencies);
      const startDate = `${fiscalYear}-04-01`;
      const endDate   = `${fiscalYear + 1}-03-31`;

      const TX_SELECT = `
        fees_without_tax, status, compromise_date,
        transaction_retrocessions(amount),
        transaction_negotiators(negotiator_id, percentage, agency)
      `;

      const [
        { data: negsRow, error: negsErr },
        { data: txReal,  error: txRErr  },
        { data: txPot,   error: txPErr  },
      ] = await Promise.all([
        supabase.from('negotiators')
          .select(`id, firstname, lastname, has_custom_objectives, monthly_objectives,
                   negotiator_annual_objectives!left(fiscal_year, annual_objective),
                   negotiator_agencies(agency, objective_percentage)`)
          .eq('archived', false)
          .neq('firstname', 'DIRECTION'),

        supabase.from('transactions').select(TX_SELECT)
          .in('status', ['compromis', 'acte', 'encaisse', 'cloture'])
          .gte('compromise_date', startDate)
          .lte('compromise_date', endDate),

        supabase.from('transactions').select(TX_SELECT)
          .eq('status', 'potentiel'),
      ]);

      if (negsErr) throw negsErr;
      if (txRErr)  throw txRErr;
      if (txPErr)  throw txPErr;

      const relevant = (negsRow || []).filter((n: any) =>
        n.negotiator_agencies?.some((na: any) => names.includes(na.agency))
      );

      const formattedNegs: NegotiatorSynthese[] = relevant.map((neg: any) => {
        const assocs = (neg.negotiator_agencies || []).filter((na: any) => names.includes(na.agency));
        const monthlyObjectives: Record<string, number> = {};

        if (neg.has_custom_objectives && neg.monthly_objectives?.[fiscalYear]) {
          const custom = neg.monthly_objectives[fiscalYear];
          MONTHS.forEach(m => {
            monthlyObjectives[m.key] = assocs.reduce((s: number, na: any) =>
              s + ((custom[m.key] || 0) * (na.objective_percentage ?? 100)) / 100, 0);
          });
        } else {
          const entry  = neg.negotiator_annual_objectives?.find((o: any) => o.fiscal_year === fiscalYear);
          const annual = entry?.annual_objective || 0;
          MONTHS.forEach(m => {
            monthlyObjectives[m.key] = assocs.reduce((s: number, na: any) =>
              s + (annual / 12 * (na.objective_percentage ?? 100)) / 100, 0);
          });
        }

        return { id: neg.id, firstname: neg.firstname, lastname: neg.lastname, monthlyObjectives };
      });

      // Map de fallback : negotiator_id → agency (depuis negotiator_agencies dans negsRow)
      const negToAgency = new Map<string, string>();
      (negsRow || []).forEach((neg: any) => {
        const assoc = (neg.negotiator_agencies || []).find((na: any) => names.includes(na.agency));
        if (assoc) negToAgency.set(neg.id, assoc.agency);
      });

      const inScope = (tn: any) => {
        const agency = tn.agency || negToAgency.get(tn.negotiator_id);
        return agency && names.includes(agency);
      };

      // Realised map: monthKey → negId → amount
      const realisedMap = buildMonthlyMap();
      (txReal || []).forEach((tx: any) => {
        if (!tx.compromise_date) return;
        const mk = String(new Date(tx.compromise_date).getMonth() + 1).padStart(2, '0');
        const mm = realisedMap.get(mk);
        if (!mm) return;
        const fees = netFees(tx);
        (tx.transaction_negotiators || [])
          .filter(inScope)
          .forEach((tn: any) => {
            mm.set(tn.negotiator_id,
              (mm.get(tn.negotiator_id) || 0) + (fees * tn.percentage) / 100);
          });
      });

      // Potentiel map: negId → total
      const potMap = new Map<string, number>();
      (txPot || []).forEach((tx: any) => {
        const fees = netFees(tx);
        (tx.transaction_negotiators || [])
          .filter(inScope)
          .forEach((tn: any) => {
            potMap.set(tn.negotiator_id,
              (potMap.get(tn.negotiator_id) || 0) + (fees * tn.percentage) / 100);
          });
      });

      setNegData(formattedNegs);
      setNegRealised(realisedMap);
      setNegPotentiel(potMap);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Unified columns ──────────────────────────────────────────────────────

  const columns = useMemo((): SyntheseColumn[] => {
    if (viewMode === 'agence') {
      return agData.map(ag => ({
        key:          ag.name,
        label:        ag.name,
        getObjective: (mk) => ag.monthlyObjectives[mk] || 0,
        getRealised:  (mk) => agRealised.get(mk)?.get(ag.name) || 0,
        getPotentiel: ()   => agPotentiel.get(ag.name) || 0,
      }));
    }
    return negData.map(neg => ({
      key:          neg.id,
      label:        `${neg.firstname} ${neg.lastname}`,
      getObjective: (mk) => neg.monthlyObjectives[mk] || 0,
      getRealised:  (mk) => negRealised.get(mk)?.get(neg.id) || 0,
      getPotentiel: ()   => negPotentiel.get(neg.id) || 0,
    }));
  }, [viewMode, agData, agRealised, agPotentiel, negData, negRealised, negPotentiel]);

  const getTotalObj  = (mk: string) => columns.reduce((s, c) => s + c.getObjective(mk), 0);
  const getTotalReal = (mk: string) => columns.reduce((s, c) => s + c.getRealised(mk), 0);
  const getTotalPot  = ()           => columns.reduce((s, c) => s + c.getPotentiel(), 0);

  const sumMonths = (months: readonly string[], fn: (mk: string) => number) =>
    months.reduce((s, mk) => s + fn(mk), 0);

  const yearTotalObj  = MONTHS.reduce((s, m) => s + getTotalObj(m.key), 0);
  const yearTotalReal = MONTHS.reduce((s, m) => s + getTotalReal(m.key), 0);
  const yearTotalPot  = getTotalPot();
  const yearRate      = pct(yearTotalReal, yearTotalObj);

  const rows: TableRow[] = QUARTERS.flatMap(q => [
    ...MONTHS.filter(m => m.quarter === q.n).map(m => ({ type: 'month' as const, monthKey: m.key, label: m.label })),
    { type: 'quarter' as const, quarterN: q.n, label: q.label, months: q.months },
  ]);

  const isEmpty = columns.length === 0;

  // ── Colors ────────────────────────────────────────────────────────────────

  const realColor = (real: number, obj: number, dark = false) => {
    if (real <= 0) return dark ? 'text-slate-500' : 'text-slate-300';
    if (obj > 0 && real >= obj) return dark ? 'text-emerald-300' : 'text-emerald-700';
    return dark ? 'text-amber-200' : 'text-slate-700';
  };

  const rateBadge = (r: number) => {
    if (r >= 100) return 'text-emerald-400';
    if (r >= 75)  return 'text-blue-300';
    if (r >= 50)  return 'text-amber-300';
    return 'text-red-400';
  };

  const pickerLabel =
    selectedAgencies.size === agencies.length ? 'Toutes les agences' :
    selectedAgencies.size === 1              ? Array.from(selectedAgencies)[0] :
    `${selectedAgencies.size} agences`;

  if (agencies.length === 0) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-8 text-center text-sm text-slate-500">
        Aucune agence dans votre périmètre.
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* ── Controls bar ── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-5 py-3">
        <div className="flex items-center gap-3 flex-wrap">

          {/* View mode toggle */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 shrink-0">
            <button
              onClick={() => setViewMode('agence')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                viewMode === 'agence' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Building2 size={13} />
              Vue agence
            </button>
            <button
              onClick={() => setViewMode('negociateur')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                viewMode === 'negociateur' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Users size={13} />
              Vue négociateur
            </button>
          </div>

          {/* Agency multi-picker */}
          {agencies.length > 1 && (
            <div className="relative shrink-0" ref={pickerRef}>
              <button
                onClick={() => setShowPicker(p => !p)}
                className={`flex items-center gap-2 pl-3 pr-2.5 py-1.5 text-sm border rounded-xl bg-white text-slate-700 hover:bg-slate-50 transition-colors ${
                  showPicker ? 'border-indigo-400 ring-2 ring-indigo-400/30' : 'border-slate-200'
                }`}
              >
                <span className="font-medium">{pickerLabel}</span>
                <ChevronDown size={13} className={`text-slate-400 transition-transform ${showPicker ? 'rotate-180' : ''}`} />
              </button>

              {showPicker && (
                <div className="absolute top-full left-0 mt-1.5 w-64 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
                  <button
                    onClick={toggleAllAgencies}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 border-b border-slate-100 transition-colors"
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                      selectedAgencies.size === agencies.length ? 'bg-indigo-500 border-indigo-500' : 'border-slate-300'
                    }`}>
                      {selectedAgencies.size === agencies.length && <Check size={10} className="text-white" />}
                    </span>
                    Toutes les agences
                  </button>
                  <div className="max-h-60 overflow-y-auto py-1">
                    {agencies.map(agency => {
                      const checked = selectedAgencies.has(agency.name);
                      return (
                        <button
                          key={agency.name}
                          onClick={() => toggleAgency(agency.name)}
                          className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                            checked ? 'bg-indigo-500 border-indigo-500' : 'border-slate-300'
                          }`}>
                            {checked && <Check size={10} className="text-white" />}
                          </span>
                          <span className="text-left truncate">{agency.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <span className="text-xs text-slate-400">Exercice {fiscalYear}–{fiscalYear + 1}</span>

          {/* KPIs */}
          {!loading && !isEmpty && (
            <div className="ml-auto flex items-center gap-3 text-xs flex-wrap justify-end">
              <span className="text-slate-500">
                Obj. : <span className="font-semibold text-slate-700">{fmt(yearTotalObj)} €</span>
              </span>
              <span className="h-3 w-px bg-slate-200" />
              <span className="text-slate-500">
                Pot. : <span className="font-semibold text-amber-600">{fmt(yearTotalPot)} €</span>
              </span>
              <span className="h-3 w-px bg-slate-200" />
              <span className="text-slate-500">
                Réal. : <span className={`font-semibold ${yearRate >= 100 ? 'text-emerald-600' : 'text-slate-700'}`}>
                  {fmt(yearTotalReal)} €
                </span>
              </span>
              <span className="h-3 w-px bg-slate-200" />
              <span className={`font-bold ${rateBadge(yearRate)}`}>{yearRate} %</span>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-16 gap-3">
          <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
          <span className="text-sm text-slate-500">Chargement…</span>
        </div>
      )}

      {!loading && !error && isEmpty && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-8 text-center text-sm text-slate-500">
          Aucune donnée à afficher pour la sélection en cours.
        </div>
      )}

      {/* ── Table ── */}
      {!loading && !error && !isEmpty && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border-collapse">

              {/* Potentiel column narrower than Obj/Réal */}
              <colgroup>
                <col style={{ minWidth: '144px' }} />
                {columns.flatMap(col => [
                  <col key={`${col.key}-obj`}  style={{ minWidth: '82px' }} />,
                  <col key={`${col.key}-pot`}  style={{ width: '62px', minWidth: '54px' }} />,
                  <col key={`${col.key}-real`} style={{ minWidth: '82px' }} />,
                ])}
                <col style={{ minWidth: '82px' }} />
                <col style={{ width: '62px', minWidth: '54px' }} />
                <col style={{ minWidth: '82px' }} />
              </colgroup>

              <thead>
                {/* Row 1: entity names (colSpan=3 each) */}
                <tr className="bg-slate-800">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider border-r border-slate-600 whitespace-nowrap w-36">
                    Période
                  </th>
                  {columns.map(col => (
                    <th
                      key={col.key}
                      colSpan={3}
                      className="px-3 py-3 text-center text-xs font-bold text-white uppercase tracking-wider border-r border-slate-600"
                    >
                      <span className="block truncate max-w-[180px] mx-auto">{col.label}</span>
                    </th>
                  ))}
                  <th colSpan={3} className="px-3 py-3 text-center text-xs font-bold text-amber-300 uppercase tracking-wider">
                    TOTAL
                  </th>
                </tr>

                {/* Row 2: sub-labels (Objectif | Potentiel | Réalisé) */}
                <tr className="bg-slate-700">
                  <th className="px-4 py-2 border-r border-slate-600" />
                  {columns.map(col => (
                    <>
                      <th key={`${col.key}-lobj`}  className="px-3 py-2 text-[11px] text-center font-medium text-slate-400 border-r border-slate-700/50 whitespace-nowrap">Objectif</th>
                      <th key={`${col.key}-lpot`}  className="px-3 py-2 text-[11px] text-center font-medium italic text-amber-400/80 border-r border-slate-700/50 whitespace-nowrap">Pot. ∑</th>
                      <th key={`${col.key}-lreal`} className="px-3 py-2 text-[11px] text-center font-medium text-slate-300 border-r border-slate-600 whitespace-nowrap">Réalisé</th>
                    </>
                  ))}
                  <th className="px-3 py-2 text-[11px] text-center font-medium text-amber-300/70 border-r border-slate-700/50 whitespace-nowrap">Objectif</th>
                  <th className="px-3 py-2 text-[11px] text-center font-medium italic text-amber-400/80 border-r border-slate-700/50 whitespace-nowrap">Pot. ∑</th>
                  <th className="px-3 py-2 text-[11px] text-center font-medium text-amber-200 whitespace-nowrap">CA Réalisé</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((row, idx) => {

                  /* ── Month row ── */
                  if (row.type === 'month') {
                    const tObj  = getTotalObj(row.monthKey);
                    const tReal = getTotalReal(row.monthKey);
                    return (
                      <tr key={row.monthKey} className={`border-b border-slate-100 ${idx % 2 === 1 ? 'bg-slate-50/60' : 'bg-white'}`}>
                        <td className="px-4 py-2.5 text-xs font-medium text-slate-500 border-r border-slate-100 whitespace-nowrap tracking-wide">
                          {row.label}
                        </td>
                        {columns.map(col => {
                          const cObj  = col.getObjective(row.monthKey);
                          const cReal = col.getRealised(row.monthKey);
                          return (
                            <>
                              <td key={`${col.key}-${row.monthKey}-obj`}  className="px-3 py-2.5 text-right text-xs text-slate-400 border-r border-slate-100/50 whitespace-nowrap">{fmt(cObj)}</td>
                              <td key={`${col.key}-${row.monthKey}-pot`} className="border-r border-slate-100/50" style={{ background: 'repeating-linear-gradient(135deg,rgba(251,191,36,.06) 0,rgba(251,191,36,.06) 3px,transparent 3px,transparent 11px)' }} />
                              <td key={`${col.key}-${row.monthKey}-real`} className={`px-3 py-2.5 text-right text-xs font-semibold border-r border-slate-100 whitespace-nowrap ${realColor(cReal, cObj)}`}>{fmt(cReal)}</td>
                            </>
                          );
                        })}
                        {/* Total */}
                        <td className="px-3 py-2.5 text-right text-xs text-slate-400 border-r border-amber-100/50 whitespace-nowrap bg-amber-50/20">{fmt(tObj)}</td>
                        <td className="border-r border-amber-100/50" style={{ background: 'repeating-linear-gradient(135deg,rgba(251,191,36,.09) 0,rgba(251,191,36,.09) 3px,transparent 3px,transparent 11px)' }} />
                        <td className={`px-3 py-2.5 text-right text-xs font-semibold whitespace-nowrap bg-amber-50/20 ${realColor(tReal, tObj)}`}>{fmt(tReal)}</td>
                      </tr>
                    );
                  }

                  /* ── Quarter row ── */
                  const qTObj  = sumMonths(row.months, getTotalObj);
                  const qTReal = sumMonths(row.months, getTotalReal);
                  const qRate  = pct(qTReal, qTObj);

                  return (
                    <tr key={`q${row.quarterN}`} className="bg-slate-100 border-t border-slate-300 border-b border-slate-300">
                      <td className="px-4 py-2.5 text-xs font-bold text-slate-700 uppercase tracking-wide border-r border-slate-300 whitespace-nowrap">
                        {row.label}
                      </td>
                      {columns.map(col => {
                        const cqObj  = sumMonths(row.months, col.getObjective);
                        const cqReal = sumMonths(row.months, col.getRealised);
                        return (
                          <>
                            <td key={`${col.key}-q${row.quarterN}-obj`}  className="px-3 py-2.5 text-right text-xs font-bold text-slate-600 border-r border-slate-300/50 whitespace-nowrap">{fmt(cqObj)}</td>
                            <td key={`${col.key}-q${row.quarterN}-pot`} className="border-r border-slate-300/50" style={{ background: 'repeating-linear-gradient(135deg,rgba(251,191,36,.11) 0,rgba(251,191,36,.11) 3px,transparent 3px,transparent 11px)' }} />
                            <td key={`${col.key}-q${row.quarterN}-real`} className={`px-3 py-2.5 text-right text-xs font-bold border-r border-slate-300 whitespace-nowrap ${realColor(cqReal, cqObj)}`}>{fmt(cqReal)}</td>
                          </>
                        );
                      })}
                      <td className="px-3 py-2.5 text-right text-xs font-bold text-slate-600 border-r border-amber-200/50 whitespace-nowrap bg-amber-50">{fmt(qTObj)}</td>
                      <td className="border-r border-amber-200/50" style={{ background: 'repeating-linear-gradient(135deg,rgba(251,191,36,.14) 0,rgba(251,191,36,.14) 3px,transparent 3px,transparent 11px)' }} />
                      <td className="px-3 py-2.5 text-right whitespace-nowrap bg-amber-50">
                        <div className="flex flex-col items-end gap-0.5">
                          <span className={`text-xs font-bold ${realColor(qTReal, qTObj)}`}>{fmt(qTReal)}</span>
                          {qTObj > 0 && <span className={`text-[10px] font-semibold ${rateBadge(qRate)}`}>{qRate} %</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {/* ── Year total row ── */}
                <tr className="bg-slate-800 border-t-2 border-slate-600">
                  <td className="px-4 py-3.5 text-xs font-black text-white uppercase tracking-widest border-r border-slate-600 whitespace-nowrap">
                    ANNÉE
                  </td>
                  {columns.map(col => {
                    const yObj  = MONTHS.reduce((s, m) => s + col.getObjective(m.key), 0);
                    const yPot  = col.getPotentiel();
                    const yReal = MONTHS.reduce((s, m) => s + col.getRealised(m.key), 0);
                    const yRate = pct(yReal, yObj);
                    return (
                      <>
                        <td key={`${col.key}-year-obj`}  className="px-3 py-3.5 text-right text-xs font-bold text-slate-300 border-r border-slate-700 whitespace-nowrap">{fmt(yObj)}</td>
                        <td key={`${col.key}-year-pot`}  className="px-3 py-3.5 text-right text-xs font-bold text-amber-300 border-r border-slate-700 whitespace-nowrap">{fmt(yPot)}</td>
                        <td key={`${col.key}-year-real`} className="px-3 py-3.5 text-right border-r border-slate-600 whitespace-nowrap">
                          <div className="flex flex-col items-end gap-0.5">
                            <span className={`text-xs font-bold ${realColor(yReal, yObj, true)}`}>{fmt(yReal)}</span>
                            {yObj > 0 && <span className={`text-[10px] font-semibold ${rateBadge(yRate)}`}>{yRate} %</span>}
                          </div>
                        </td>
                      </>
                    );
                  })}
                  {/* Total columns */}
                  <td className="px-3 py-3.5 text-right text-xs font-bold text-amber-200 border-r border-amber-900/40 whitespace-nowrap bg-amber-900/20">{fmt(yearTotalObj)}</td>
                  <td className="px-3 py-3.5 text-right text-xs font-bold text-amber-300 border-r border-amber-900/40 whitespace-nowrap bg-amber-900/20">{fmt(yearTotalPot)}</td>
                  <td className="px-3 py-3.5 text-right whitespace-nowrap bg-amber-900/20">
                    <div className="flex flex-col items-end gap-0.5">
                      <span className={`text-xs font-bold ${realColor(yearTotalReal, yearTotalObj, true)}`}>{fmt(yearTotalReal)}</span>
                      {yearTotalObj > 0 && <span className={`text-[10px] font-semibold ${rateBadge(yearRate)}`}>{yearRate} %</span>}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Footnote */}
      {!loading && !isEmpty && (
        <p className="text-[11px] text-slate-400 italic text-right px-1">
          ∑ Potentiel = pipeline en cours au moment de la consultation — non ventilable par mois
        </p>
      )}
    </div>
  );
}
